import { z } from 'zod'
import { rbacSlugSchema } from './rbac'

/**
 * Member & invitation management shapes — the `/api/rbac/members` and `/api/rbac/invitations`
 * routes, backing the Users tab of the access page.
 *
 * Like roles and permissions, none of this lives in our database. Members are WorkOS
 * organization memberships (the thing that grants access at all, now that sessions without one
 * are rejected); invitations are WorkOS invitations, whose acceptance is what CREATES a
 * membership. WorkOS sends the invitation email and hosts the accept flow.
 */

export const memberSchema = z.object({
  /** WorkOS membership id (`om_…`) — the handle for role changes and removal. */
  membershipId: z.string(),
  email: z.string().nullable(),
  name: z.string().nullable(),
  /**
   * Org-wide role slug from the membership. Always present — WorkOS has no membership without a
   * role — so an external user carries `client`, which grants nothing. Display + role-change
   * plumbing; never an authz input.
   */
  role: z.string(),
  status: z.string(),
  /**
   * True when this row is the caller. The server refuses self role-changes and self-removal
   * (lockout guards); the UI uses this to disable those controls rather than offer a 400.
   */
  isSelf: z.boolean(),
  createdAt: z.string(),
})

export const invitationSchema = z.object({
  id: z.string(),
  email: z.string(),
  state: z.enum(['pending', 'accepted', 'expired', 'revoked']),
  /** Role the invitee lands with on acceptance. */
  roleSlug: z.string().nullable(),
  expiresAt: z.string(),
  createdAt: z.string(),
})

export const inviteUserSchema = z.object({
  email: z.email(),
  /**
   * The org-wide role the invitee lands with. Null means `client` — the role that grants nothing —
   * and the API sends it by name rather than leaving WorkOS to apply the environment's default.
   *
   * **Anything but `client` is a staff decision.** An org-wide role is carried in the session JWT
   * and reaches every tenant in the environment; a customer's admin must never hold one that
   * carries permissions. Their access is FGA grants on their own account, issued from that
   * account's access section.
   */
  roleSlug: rbacSlugSchema.nullable(),
})

export const updateMemberRoleSchema = z.object({
  /** Moving someone to `client` is how an internal user becomes an external one. */
  roleSlug: rbacSlugSchema,
})

export const listMembersResponseSchema = z.object({
  members: z.array(memberSchema),
})

export const listInvitationsResponseSchema = z.object({
  invitations: z.array(invitationSchema),
})

export type Member = z.infer<typeof memberSchema>
export type Invitation = z.infer<typeof invitationSchema>
export type InviteUser = z.infer<typeof inviteUserSchema>
export type UpdateMemberRole = z.infer<typeof updateMemberRoleSchema>
export type ListMembersResponse = z.infer<typeof listMembersResponseSchema>
export type ListInvitationsResponse = z.infer<typeof listInvitationsResponseSchema>
